import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import { mutations, queriesV2 } from '../../../api';
import { useNotifications } from '../../../notifications';
import { TitleFormattedMessage } from '../../../components/titleFormattedMessage';
import PropdocsModal from '../../../components/modal/PropdocsModal';

type RenameFolderModalProps = {
  isOpen: boolean;
  onClose: () => void;
  folderId: string;
  currentName: string;
  teamId: string;
  onSuccess: () => void;
  folderType?: string;
};

const RenameFolderModal = ({
  isOpen,
  onClose,
  folderId,
  currentName,
  teamId,
  onSuccess,
  folderType = 'template',
}: RenameFolderModalProps) => {
  const { formatMessage } = useIntl();
  const [newName, setNewName] = useState(currentName);
  const [isLoading, setIsLoading] = useState(false);
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();

  // TODO: Replace with actual rename folder mutation when available
  const [updateTemplateFolder] = useMutation(mutations.updateTemplateFolder as any, {
    onCompleted: () => {
      notifySuccess({ message: 'Folder renamed successfully' });
      onSuccess();
      onClose();
      setIsLoading(false);
    },
    onError: (error) => {
      notifyError({ message: error.message || 'Failed to rename folder' });
      setIsLoading(false);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim() || newName.trim() === currentName) {
      return;
    }

    setIsLoading(true);
    try {
      // TODO: Replace with actual mutation variables when backend is ready
      await updateTemplateFolder({
        variables: {
          id: folderId,
          name: newName.trim(),
        },
        refetchQueries: [
          {
            query: queriesV2.getTemplateFolders,
            variables: { teamId, folderType },
          },
        ],
      });
    } catch (error) {
      console.error('Error renaming folder:', error);
      notifyError({ message: 'Failed to rename folder' });
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setNewName(currentName);
    onClose();
  };

  return (
    <PropdocsModal
      show={isOpen}
      setShow={handleClose}
      size="sm"
      title={<FormattedMessage id="title.rename-folder" defaultMessage="Rename Folder" />}
      disableClose={isLoading}
    >
      <Form onSubmit={handleSubmit}>
        <>
          <div style={{ marginBottom: '1.5rem' }}>
            <Form.Group>
              <Form.Label>
                <FormattedMessage id="label.folder-name" defaultMessage="Folder Name" />
              </Form.Label>
              <Form.Control
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={formatMessage({
                  id: 'placeholder.folder-name',
                  defaultMessage: 'Enter folder name',
                })}
                autoFocus
                required
                maxLength={100}
              />
            </Form.Group>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1.5rem' }}>
            <Button variant="link" className="text-secondary" onClick={handleClose} disabled={isLoading}>
              <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={isLoading || !newName.trim() || newName.trim() === currentName}
            >
              {isLoading ? (
                <FormattedMessage id="cta.renaming" defaultMessage="Renaming..." />
              ) : (
                <TitleFormattedMessage id="cta.rename" defaultMessage="Rename" />
              )}
            </Button>
          </div>
        </>
      </Form>
    </PropdocsModal>
  );
};

export default RenameFolderModal;
